// Mock implementation of:
//   GET    /api/secure/patient/profiles
//   POST   /api/secure/patient/profiles
//   GET    /api/secure/patient/profiles/:id
//   PATCH  /api/secure/patient/profiles/:id
//   POST   /api/secure/patient/profiles/:id/archive
//
// Multi-patient identity model — see docs/PATIENT_PORTAL_PLAN.md §3.
// Every PatientProfile carries `personIdentityId` (nullable in v1) so the schema
// is forward-compatible with §3.6 (PersonIdentity layer arrives in v2).

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.profiles.v1'

const seedProfiles = () => [
  {
    id: 'pp_self_brandA',
    brandId: 'brand_a',
    brandName: 'Sunrise Wellness',
    brandLogoUrl: 'https://api.dicebear.com/7.x/initials/svg?seed=Sunrise%20Wellness&backgroundColor=E8EEF6&textColor=1F3A5F',
    patientNumber: 'SW-2025-0428',
    personIdentityId: null, // see §3.6 — populated lazily in v2
    displayName: 'You — Sunrise Wellness',
    relation: 'SELF',
    avatarColor: '#1F3A5F',
    avatarInitials: 'YO',
    dob: '1992-08-14',
    gender: 'PREFER_NOT',
    primaryUserId: 'mock_user_1',
    emergencyContact: { name: 'Priya', phone: '+91-98xxxxxx12', relation: 'SPOUSE' },
    allergies: ['Penicillin'],
    medicalHistory: 'Mild GAD, on SSRI maintenance.',
    role: 'OWNER',
    createdAt: '2025-09-01T08:00:00Z',
    archivedAt: null,
  },
  {
    id: 'pp_self_brandB',
    brandId: 'brand_b',
    brandName: 'Greenleaf Clinic',
    brandLogoUrl: 'https://api.dicebear.com/7.x/initials/svg?seed=Greenleaf%20Clinic&backgroundColor=E1ECE7&textColor=3F7D58',
    patientNumber: 'GLC-241112-0091',
    personIdentityId: null,
    displayName: 'You — Greenleaf Clinic',
    relation: 'SELF',
    avatarColor: '#6B9080',
    avatarInitials: 'YO',
    dob: '1992-08-14',
    gender: 'PREFER_NOT',
    primaryUserId: 'mock_user_1',
    emergencyContact: { name: 'Priya', phone: '+91-98xxxxxx12', relation: 'SPOUSE' },
    allergies: ['Penicillin'],
    medicalHistory: 'Routine GP follow-ups; recent lipid panel WNL.',
    role: 'OWNER',
    createdAt: '2025-11-12T08:00:00Z',
    archivedAt: null,
  },
  {
    id: 'pp_mom_brandA',
    brandId: 'brand_a',
    brandName: 'Sunrise Wellness',
    brandLogoUrl: 'https://api.dicebear.com/7.x/initials/svg?seed=Sunrise%20Wellness&backgroundColor=E8EEF6&textColor=1F3A5F',
    patientNumber: 'SW-2025-0211',
    personIdentityId: null,
    displayName: 'Mom',
    relation: 'PARENT',
    avatarColor: '#B97A2A',
    avatarInitials: 'MO',
    dob: '1962-04-03',
    gender: 'F',
    primaryUserId: 'mock_user_1',
    emergencyContact: { name: 'You', phone: '+91-98xxxxxx00', relation: 'CHILD' },
    allergies: [],
    medicalHistory: 'Type 2 diabetes, hypertension. Post-op knee replacement (2024).',
    role: 'OWNER',
    createdAt: '2025-10-18T08:00:00Z',
    archivedAt: null,
  },
]

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch (e) {
    // fall through to seeding
  }
  const seeded = seedProfiles()
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded))
  } catch (e) {
    // private mode, etc — return seeded in-memory
  }
  return seeded
}

function writeStore(profiles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles))
  } catch (e) {
    // ignore
  }
}

export async function listProfiles() {
  await delay()
  return readStore().filter((p) => !p.archivedAt)
}

export async function getProfile(id) {
  await delay()
  const profile = readStore().find((p) => p.id === id)
  if (!profile) {
    const error = new Error('Profile not found')
    error.status = 404
    throw error
  }
  return profile
}

export async function createProfile(input) {
  await delay()
  const profiles = readStore()
  const newProfile = {
    id: `pp_new_${Date.now()}`,
    brandId: input.brandId || null,
    brandName: input.brandName || 'New Brand',
    brandLogoUrl: input.brandLogoUrl || null,
    patientNumber: input.patientNumber || null,
    personIdentityId: null,
    displayName: input.displayName || 'New profile',
    relation: input.relation || 'OTHER',
    avatarColor: input.avatarColor || '#1F3A5F',
    avatarInitials: (input.displayName || 'NP').slice(0, 2).toUpperCase(),
    dob: input.dob || null,
    gender: input.gender || null,
    primaryUserId: 'mock_user_1',
    emergencyContact: input.emergencyContact || null,
    allergies: input.allergies || [],
    medicalHistory: input.medicalHistory || '',
    role: 'OWNER',
    createdAt: new Date().toISOString(),
    archivedAt: null,
  }
  const next = [...profiles, newProfile]
  writeStore(next)
  return newProfile
}

export async function updateProfile(id, patch) {
  await delay()
  const profiles = readStore()
  const idx = profiles.findIndex((p) => p.id === id)
  if (idx === -1) {
    const error = new Error('Profile not found')
    error.status = 404
    throw error
  }
  const updated = { ...profiles[idx], ...patch }
  const next = [...profiles]
  next[idx] = updated
  writeStore(next)
  return updated
}

export async function archiveProfile(id) {
  return updateProfile(id, { archivedAt: new Date().toISOString() })
}

// Test helper — wipes the local store back to seeded defaults.
export function __resetProfilesMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}
